/**
 * TikTok Ads Performance dashboard page.
 * Passes TikTok-specific config to the generic PlatformDashboard.
 */
import React from 'react';
import { useTranslation } from 'src/arb-wrapper';
import type { PaidMediaInsightsConfig } from 'src/components/paid-media-insights/InsightsSection';
import PlatformDashboard from 'src/components/paid-media-dashboard/PlatformDashboard';
import { formatPercent } from 'src/utils/dashboardUtils';

const TIKTOK_TABS = [
  { value: 'campaign', label: 'Campaign' },
  { value: 'adgroup', label: 'Ad Group' },
  { value: 'ad', label: 'Ad' },
  { value: 'product', label: 'Product' },
];

const TIKTOK_BREAKDOWN_OPTIONS = [
  { value: 'age_gender', label: 'Demographics Breakdown' },
];

const TIKTOK_COLUMN_WIDTHS_OVERRIDES: Record<string, number> = {
  campaign_name: 600,
  adgroup_name: 600,
  ad_name: 600,
};

// Display CTR as percentage (backend returns decimal ratio e.g. 0.05 → 5.00%)
const TIKTOK_FORMAT_OVERRIDES = {
  ctr: (value: unknown) => formatPercent(value),
};

const TIKTOK_SUMMARY_COLUMNS = [
  'campaign_name', 'adgroup_name', 'ad_name', 'advertiser_name', 'objective_type', 'age', 'gender',
  'currency', 'spend', 'impressions', 'clicks', 'ctr', 'cpc', 'cpm',
  'reach', 'video_watched_6s', 'frequency', 'video_views_p100',
  'likes', 'comments', 'shares', 'profile_visits', 'follows',
  'product_id', 'product_set_id', 'product_set_name', 'gtis', 'titles', 'en_titles', 'reftags',
];

const TIKTOK_ALWAYS_VISIBLE_KEYS = ['campaign_name', 'adgroup_name', 'ad_name'];

const TIKTOK_CURRENCY_COL_KEY = 'currency';

const TIKTOK_INSIGHTS_CONFIG = {
  cardMetrics: [
    {
      key: 'spend',
      label: 'paid_media_insights_metric_spend',
      numberFormat: {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        style: 'currency',
      },
    },
    {
      key: 'impressions',
      label: 'paid_media_insights_metric_impressions',
      numberFormat: { maximumFractionDigits: 0, style: 'decimal' },
    },
    {
      key: 'clicks',
      label: 'paid_media_insights_metric_clicks',
      numberFormat: { maximumFractionDigits: 0, style: 'decimal' },
    },
    {
      key: 'ctr',
      label: 'paid_media_insights_metric_ctr',
      numberFormat: { maximumFractionDigits: 2, style: 'percent' },
    },
  ],
  engagementMetrics: [
    { key: 'likes', label: 'paid_media_insights_metric_likes' },
    { key: 'comments', label: 'paid_media_insights_metric_comments' },
    { key: 'shares', label: 'paid_media_insights_metric_shares' },
  ],
} satisfies PaidMediaInsightsConfig;

const TikTokDashboard: React.FC = () => {
  const { t } = useTranslation();

  const columnTooltips: Record<string, string> = {
    campaign_name: t('paid_media_tooltip_campaign_name'),
    advertiser_name: t('paid_media_tooltip_tiktok_advertiser_name'),
    objective_type: t('paid_media_tooltip_tiktok_objective_type'),
    adgroup_name: t('paid_media_tooltip_tiktok_adgroup_name'),
    ad_name: t('paid_media_tooltip_ad_name'),
    currency: t('paid_media_tooltip_tiktok_currency'),
    spend: t('paid_media_tooltip_spend'),
    impressions: t('paid_media_tooltip_impressions'),
    clicks: t('paid_media_tooltip_clicks'),
    ctr: t('paid_media_tooltip_ctr'),
    cpc: t('paid_media_tooltip_cpc'),
    cpm: t('paid_media_tooltip_cpm'),
    reach: t('paid_media_tooltip_reach'),
    frequency: t('paid_media_tooltip_frequency'),
    engagements: t('paid_media_tooltip_tiktok_engagements'),
    likes: t('paid_media_tooltip_tiktok_likes'),
    comments: t('paid_media_tooltip_tiktok_comments'),
    shares: t('paid_media_tooltip_tiktok_shares'),
    follows: t('paid_media_tooltip_tiktok_follows'),
    profile_visits: t('paid_media_tooltip_tiktok_profile_visits'),
    video_play_actions: t('paid_media_tooltip_tiktok_video_play_actions'),
    video_watched_2s: t('paid_media_tooltip_tiktok_video_watched_2s'),
    video_watched_6s: t('paid_media_tooltip_tiktok_video_watched_6s'),
    video_views_p25: t('paid_media_tooltip_tiktok_video_views_p25'),
    video_views_p50: t('paid_media_tooltip_tiktok_video_views_p50'),
    video_views_p75: t('paid_media_tooltip_tiktok_video_views_p75'),
    video_views_p100: t('paid_media_tooltip_tiktok_video_views_p100'),
  };

  return (
    <PlatformDashboard
      alwaysVisibleKeys={TIKTOK_ALWAYS_VISIBLE_KEYS}
      breakdownExcludedTabs={['product']}
      breakdownOptions={TIKTOK_BREAKDOWN_OPTIONS}
      columnTooltips={columnTooltips}
      columnWidths={TIKTOK_COLUMN_WIDTHS_OVERRIDES}
      currencyColumnKey={TIKTOK_CURRENCY_COL_KEY}
      formatOverrides={TIKTOK_FORMAT_OVERRIDES}
      insightsConfig={TIKTOK_INSIGHTS_CONFIG}
      summaryColumns={TIKTOK_SUMMARY_COLUMNS}
      tabs={TIKTOK_TABS}
      title="TikTok Ads Performance"
      titleSearchSubViews={['product']}
      view="tiktok"
    />
  );
};

export default TikTokDashboard;
